import { differenceInSeconds, parseISO } from 'date-fns'
import { useFormikContext } from 'formik'

import {
  getNumberOfUnitsInLot,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import type { BidBasis, BidPrecision } from '@b-stock/fe-core/bstock-next/hooks'
import type { OrchestratedAuctionListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { usePrecisionNumberFormat } from '@b-stock/fe-core/bstock-react'

import {
  type BidFormFormikValues,
  useBidFormContext,
} from '@/buyer-portal/components/features/listing/actions/auction/BidFormProvider'
import { useCostContextAnalyticsProperties } from '@/buyer-portal/components/features/listing/hooks/useCostContextAnalyticsProperties'
import { useProblemFreeOrderRateAnalytics } from '@/buyer-portal/components/features/listing/hooks/useProblemFreeOrderRateAnalytics'
import {
  QuickActionButton,
  QuickActionButtonRow,
} from '@/buyer-portal/components/features/listing/ListingActionColumn/actionCard/ui'
import getListingAnalyticsData from '@/buyer-portal/helpers/getListingAnalyticsData'
import { useListingP2SData } from '@/buyer-portal/hooks/useListingP2SData'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

/**
 * Calculate the next whole bid increment that is greater than or equal to the target amount.
 * Rounds up to the nearest hundred for dollar amounts >= 100, otherwise uses bidPrecision.
 *
 * RELATIVE bids are percentages, not dollars, so the round-up-to-$100 step never applies
 * to them: it would round a correct 110% up to 200%. Their bidPrecision is already 1
 * (one percentage point), which is the increment they round to.
 */
const calculateNextBidIncrement = (
  targetAmount: number,
  bidPrecision: number,
  bidBasis: BidBasis
): number => {
  // For dollar amounts >= 100, round to the nearest hundred
  if (bidBasis !== 'RELATIVE' && targetAmount >= 100) {
    return Math.ceil(targetAmount / 100) * 100
  }

  // For smaller amounts, round to the bid precision.
  // Use toFixed based on bidPrecision's decimal places to avoid floating-point
  // artifacts (e.g. 14829 * 0.0001 = 1.4828000000000001 in IEEE 754).
  const decimals = bidPrecision.toString().split('.')[1]?.length ?? 0
  return parseFloat(
    (Math.ceil(targetAmount / bidPrecision) * bidPrecision).toFixed(decimals)
  )
}

/**
 * Calculate quick bid suggestions based on current price and percentage increases.
 * Ensures all three values are unique and progressively higher.
 */
export const calculateQuickBidSuggestions = (
  currentPrice: number,
  bidPrecision: number,
  bidBasis: BidBasis
): [number, number, number] => {
  const twentyPercent = currentPrice * 1.2
  const fiftyPercent = currentPrice * 1.5
  const hundredPercent = currentPrice * 2.0

  const bid20 = calculateNextBidIncrement(twentyPercent, bidPrecision, bidBasis)
  let bid50 = calculateNextBidIncrement(fiftyPercent, bidPrecision, bidBasis)
  let bid100 = calculateNextBidIncrement(hundredPercent, bidPrecision, bidBasis)

  // Ensure bid50 is higher than bid20 (add one increment if needed)
  const increment = bidBasis !== 'RELATIVE' && bid20 >= 100 ? 100 : bidPrecision
  if (bid50 <= bid20) {
    bid50 = bid20 + increment
  }

  // Ensure bid100 is higher than bid50 (add one increment if needed)
  if (bid100 <= bid50) {
    bid100 = bid50 + increment
  }

  return [bid20, bid50, bid100]
}

type QuickBidButtonsProps = {
  listing: OrchestratedAuctionListingDetails
  bidBasis: BidBasis
  bidPrecision?: BidPrecision
  currency?: string
  disabled?: boolean
  userMinBidAmount: number
}

export const QuickBidButtons = ({
  listing,
  bidBasis,
  bidPrecision = 1,
  currency = 'USD',
  disabled = false,
  userMinBidAmount,
}: QuickBidButtonsProps) => {
  // All hooks must be called unconditionally at the top
  const fmvBadgeFlag = useFlag(FeatureFlags.ENABLE_FMV_DEAL_BADGE)
  const isTlcDisplayed =
    !!listing.lot.retailValue || (getNumberOfUnitsInLot(listing.lot) ?? 0) > 0
  const { setFieldValue, submitForm } = useFormikContext<BidFormFormikValues>()
  const { formatPrecisionNumber } = usePrecisionNumberFormat({
    currency,
    format: bidBasis === 'RELATIVE' ? 'percent' : 'currency',
    precision: bidPrecision,
    precisionStored: 1,
  })
  const { trackButtonClicked } = useAnalytics()

  // Fetch P2S analytics data
  const p2sData = useListingP2SData(listing.lot.locationOriginalId)

  const problemFreeOrderRateAnalytics =
    useProblemFreeOrderRateAnalytics(listing)

  const { selectedQuote } = useBidFormContext()
  const costContext = useCostContextAnalyticsProperties({
    listing,
    selectedQuote,
  })

  // Anchor to winning bid + minimum bid increment
  const winningBidAmount = listing.auction.winningBidAmount ?? 0
  const bidIncrement = userMinBidAmount - winningBidAmount
  const button1Amount = userMinBidAmount
  const button2Amount = winningBidAmount + bidIncrement * 2
  const button3Amount = winningBidAmount + bidIncrement * 5

  // RELATIVE (percentage) bids can never exceed 100% — a bid above 100% of
  // the basis is rejected server-side ("Please enter a bid amount of 100% or
  // less."). Cap every suggestion at 100% and drop the duplicates that
  // produces, so a quick bid button above 100% is never displayed and the
  // highest quick bid offered is 100% (BUGS-5433). Non-RELATIVE (dollar) bids
  // keep all three suggestions unchanged.
  const quickBidAmounts =
    bidBasis === 'RELATIVE'
      ? [button1Amount, button2Amount, button3Amount]
          .map((amount) => Math.min(amount, 100))
          .filter((amount, index, amounts) => amounts.indexOf(amount) === index)
          .filter((amount) => amount >= userMinBidAmount)
      : [button1Amount, button2Amount, button3Amount]

  const handleQuickBidClick = async (
    amount: number,
    buttonNumber: 1 | 2 | 3
  ) => {
    // Calculate time left until auction ends
    const timeDiffInSeconds = differenceInSeconds(
      // oxlint-disable-next-line react/purity
      parseISO(listing.auction.actualEndTime || new Date().toDateString()),
      // oxlint-disable-next-line react/purity
      new Date()
    )

    // Track the quick_bid button click event
    trackButtonClicked({
      buttonName: 'quick_bid',
      button_number: buttonNumber,
      screenName: 'listing_detail_page',
      bid_amt_usd: amount,
      bids_existing: listing.auction.numberOfBids,
      time_left: timeDiffInSeconds,
      quick_bid_options: quickBidAmounts,
      bid_type: 'quick_bid',
      is_new_bid_box_design: true,
      is_total_landed_cost_displayed: isTlcDisplayed,
      ...getListingAnalyticsData(listing, fmvBadgeFlag, p2sData),
      ...problemFreeOrderRateAnalytics,
      ...costContext.properties,
    })

    // Set the bidType to indicate this is a quick bid
    await setFieldValue('bidType', 'quick_bid')
    await setFieldValue('quickBidButtonNumber', buttonNumber)
    // Set the field value
    await setFieldValue('amount', amount)
    // Trigger form submission which opens the bid confirmation modal
    await submitForm()
  }

  return (
    <QuickActionButtonRow>
      {quickBidAmounts.map((amount, index) => (
        <QuickActionButton
          disabled={disabled}
          key={amount}
          onClick={() => handleQuickBidClick(amount, (index + 1) as 1 | 2 | 3)}
          type="button"
        >
          {formatPrecisionNumber(amount)}
        </QuickActionButton>
      ))}
    </QuickActionButtonRow>
  )
}
